import { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { useDateRange } from '@shared/state/dateRangeStore';
import { useAccount } from '@shared/state/accountStore';
import { useFirstTrade } from '@features/dashboard/hooks/useFirstTrade';
import { useConnectDialogStore } from '@features/accounts/state/connectDialogStore';
import { ACCOUNTS_PANEL_WIDTH_PX } from '@shared/components/layout/layoutMetrics';
import { DateRangePicker } from '@shared/ui/DateRangePicker';
import { Tooltip } from '@shared/ui';

/** Format a `YYYY-MM-DD` day as e.g. `Jun 7`. */
function fmt(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const NO_TRADES_HINT =
  'You need trades before you can pick a date range. Import a file or connect an account to get started.';

/**
 * Top-bar **Date ▾** control — a pill showing the active global range that opens the two-month
 * calendar range picker (`DateRangePicker`). Self-contained popover (outside-click + Escape) so the
 * panel can wear the exact Figma card chrome (24px pad, rounded-12, Elevation-2 shadow) rather than
 * the shared `Popover`'s default `rounded-lg p-3`.
 *
 * When the selected account has **no trades** (`/stats/first-trade` → `firstTradeAt: null`), there's
 * nothing to scope, so the pill is disabled and hovering it explains why (a range with no data would
 * only ever be empty). The picker unlocks automatically on the first import (`['first-trade']` is
 * invalidated on `trades.changed`).
 */
export function DateRangeTrigger() {
  const range = useDateRange();
  const account = useAccount();
  const firstTrade = useFirstTrade({ account: account || undefined });
  const noTrades = firstTrade.isSuccess && firstTrade.data?.firstTradeAt == null;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // The Accounts panel occupies the right 400px; when it's open, shift the calendar popover left so
  // it opens to the LEFT of the panel instead of behind it. Slides back when the panel closes.
  const panelOpen = useConnectDialogStore(s => s.panelOpen);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (!rootRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // 2026-07 Figma top-bar pill: 36px tall, canvas (#ECF1FC) fill, 13px label + chevron.
  const pillClass = 'inline-flex h-9 items-center gap-2 rounded-lg px-3 text-[13px] text-text';

  // No trades → a disabled, explained pill (the Tooltip wraps its own button, so it shows the hint on
  // hover/focus/tap even though there's no calendar to open).
  if (noTrades) {
    return (
      <Tooltip
        text={NO_TRADES_HINT}
        side="bottom"
        className={`${pillClass} bg-canvas-inset cursor-not-allowed opacity-50`}
        trigger={
          <>
            <Calendar className="h-4 w-4 text-text-muted" aria-hidden />
            <span className="whitespace-nowrap">{fmt(range.from)} – {fmt(range.to)}</span>
            <ChevronDown className="h-3.5 w-3.5 text-text-muted" aria-hidden />
          </>
        }
      />
    );
  }

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={pillClass}
        style={{ background: 'var(--canvas)' }}
      >
        <Calendar className="h-4 w-4 text-text-muted" aria-hidden />
        <span className="whitespace-nowrap">{fmt(range.from)} – {fmt(range.to)}</span>
        <ChevronDown className="h-3.5 w-3.5 text-text-muted" aria-hidden />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Pick a date range"
          className="absolute z-40 mt-2 rounded-xl p-6 transition-[right] duration-300 ease-out"
          style={{
            // Anchored to the trigger's right edge; shifted left by the panel width (+ a gap) while the
            // Accounts panel is open so it clears the panel, sliding back to right-0 when it closes.
            right: panelOpen ? ACCOUNTS_PANEL_WIDTH_PX + 8 : 0,
            background: 'var(--canvas-raised)',
            boxShadow: '0 6px 8px rgba(0, 0, 0, 0.04)',
            border: '1px solid var(--canvas-divider)',
          }}
        >
          <DateRangePicker onCommit={() => setOpen(false)} onCancel={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}
